// services/entities/useNotifications.ts

import type {
  NotificationInsertSupa,
  NotificationSupa,
  NotificationUpdateSupa,
} from "@shared/supabase.types.ts";
import useSWR, { type SWRConfiguration } from "swr";
import { supabaseClient } from "@/lib/supabase";
import { createAnalytic } from "@/services/entities/use-analytics";
import {
  createNotification as createNotificationUtil,
  getAllNotifications,
  getClientNotifications,
  getTutorNotifications,
  sendEmail,
  sendText,
} from "@/utils/notification-utilities";
import { useAuth } from "../auth/auth-context";

// Define notification role type
export enum NotificationRoleType {
  CLIENT = "CLIENT",
  TUTOR = "TUTOR",
  ALL = "ALL",
}

// Define urgency levels for easier reference
enum NotificationUrgency {
  STANDARD = 1,
  URGENT = 2,
  EXTREMELY_URGENT = 3,
}

// SWR global configuration
const swrConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  dedupingInterval: 30000, // 30 seconds - notifications should be relatively fresh
  focusThrottleInterval: 15000, // 15 seconds
  errorRetryCount: 3,
  shouldRetryOnError: true,
};

import { NOTIFICATIONS_CACHE_KEYS } from "@/services/entities/cache-keys";

export async function fetchAllNotifications(staffId?: string, userId?: string, is_read = false) {
  let query = supabaseClient.from("notifications").select("*");

  if (staffId) {
    query = query.eq("staff_id", staffId);
  } else if (userId) {
    query = query.eq("user_id", userId);
  } else {
    // If neither staffId nor userId is provided, return an empty array or throw an error
    console.warn("fetchAllNotifications called without staffId or userId.");
    return [];
  }

  const res = await query.eq("is_read", is_read).order("created_at", { ascending: false });
  if (res.error) {
    console.error("Error fetching all notifications:", res.error);
    throw res.error;
  }
  return res.data;
}

async function updateNotification(notificationId: number, input: NotificationUpdateSupa) {
  const res = await supabaseClient
    .from("notifications")
    .update(input)
    .eq("id", notificationId)
    .select()
    .single();
  return res.data;
}

export async function createNotification(input: NotificationInsertSupa) {
  const res = await supabaseClient.from("notifications").insert(input).select().single();
  if (res.error) {
    console.error("Error creating notification:", res.error);
    throw res.error;
  }
  return res.data;
}

/**
 * Hook to access and manage notifications based on user role.
 * Automatically fetches notifications based on the authenticated user's role.
 *
 * @param roleType Override the default role detection with a specific role
 * @param is_read Whether to fetch read notifications (default: false)
 * @returns Notification data and management functions
 */
export function useNotifications(
  roleType: NotificationRoleType = NotificationRoleType.ALL,
  is_read = false,
) {
  const { authUser, isTutor, isClientOnly } = useAuth();

  // Determine the effective role type
  const effectiveRoleType =
    roleType ||
    (isTutor
      ? NotificationRoleType.TUTOR
      : isClientOnly
        ? NotificationRoleType.CLIENT
        : NotificationRoleType.ALL);

  // Generate appropriate cache key and fetcher based on role
  const getCacheKeyAndFetcher = () => {
    const userId = authUser?.id;

    switch (effectiveRoleType) {
      case NotificationRoleType.TUTOR:
        return {
          key: userId ? NOTIFICATIONS_CACHE_KEYS.TUTOR_NOTIFICATIONS(userId, is_read) : null,
          fetcher: () => getTutorNotifications(userId!, is_read),
        };
      case NotificationRoleType.CLIENT:
        return {
          key: userId ? NOTIFICATIONS_CACHE_KEYS.TUTOR_NOTIFICATIONS(userId, is_read) : null,
          fetcher: () => getClientNotifications(userId!, is_read),
        };
      default:
        return {
          key: NOTIFICATIONS_CACHE_KEYS.ALL_NOTIFICATIONS(is_read),
          fetcher: () => getAllNotifications(is_read),
        };
    }
  };

  const { key, fetcher } = getCacheKeyAndFetcher();

  // Main SWR hook for notifications
  const {
    data: notifications = [],
    error,
    isLoading,
    mutate: refreshNotifications,
  } = useSWR<NotificationSupa[]>(key, fetcher, swrConfig);

  /**
   * Get unread notifications count
   */
  const unreadCount = notifications.filter((notification) => !notification.is_read).length;

  /**
   * Create a new notification with SMS and email capabilities
   *
   * @param title Notification title
   * @param message Notification message
   * @param urgency Urgency level (1=standard, 2=urgent with SMS, 3=extremely urgent with SMS and email)
   * @param staffId ID of staff (can be undefined)
   * @param userId ID of user (can be undefined)
   * @param sendEmailOrText Whether to send SMS/email for urgent notifications
   * @returns Promise<void>
   */
  const createNewNotification = async (
    title: string,
    message: string,
    urgency: NotificationUrgency,
    staffId?: string,
    userId?: string,
    sendEmailOrText = true,
  ) => {
    try {
      await createNotificationUtil(title, message, urgency, staffId, userId, sendEmailOrText);

      // Refresh notifications based on who should receive it
      if (
        (staffId && isTutor && staffId === authUser?.id) ||
        (userId && isClientOnly && userId === authUser?.id)
      ) {
        await refreshNotifications();
      }
    } catch (error) {
      console.error("Error creating notification:", error);
      // Log the error
      await createAnalytic({
        class: "ERROR",
        event: {
          event: "createNewNotification",
          error,
          title,
          message,
          urgency,
          staffId,
          userId,
        },
      });
      throw error;
    }
  };

  /**
   * Delete a notification permanently
   *
   * @param notificationId ID of the notification to delete
   * @returns Promise<void>
   */
  const deleteNotification = async (notificationId: number): Promise<void> => {
    try {
      // Optimistically update the local cache immediately
      await refreshNotifications(
        (current) =>
          current?.filter((notification) => notification.notification_id !== notificationId),
        false, // false means don't revalidate with the server yet
      );

      // delete in the db
      await supabaseClient.from("notifications").delete().eq("id", notificationId);

      // Log the successful deletion
      await createAnalytic({
        class: "LOG",
        event: {
          event: "deleteNotification",
          notificationId,
        },
      });
    } catch (error) {
      console.error("Error deleting notification:", error);

      // If deletion fails, revert the optimistic update by refreshing from the server
      await refreshNotifications();

      await createAnalytic({
        class: "ERROR",
        event: {
          event: "deleteNotification",
          error,
          notificationId,
        },
      });
      throw error;
    }
  };

  /**
   * Mark a notification as read
   *
   * @param notificationId ID of the notification to mark as read
   * @returns Promise<void>
   */
  const markAsRead = async (notificationId: number): Promise<void> => {
    try {
      await updateNotification(notificationId, {
        is_read: true,
      });

      await refreshNotifications(
        (current) =>
          current?.map((notification) =>
            notification.notification_id === notificationId
              ? { ...notification, status: "READ" }
              : notification,
          ),
        false,
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
      await createAnalytic({
        class: "ERROR",
        event: {
          event: "markNotificationAsRead",
          error,
          notificationId,
        },
      });
      throw error;
    }
  };

  /**
   * Mark all notifications as read
   *
   * @returns Promise<void>
   */
  const markAllAsRead = async (): Promise<void> => {
    try {
      // Create an array of promises for each update operation
      const updatePromises = notifications
        .filter((notification) => !notification.is_read)
        .map((notification) =>
          updateNotification(notification.notification_id, {
            is_read: true,
          }),
        );

      await Promise.all(updatePromises);

      // Update local data without revalidation
      await refreshNotifications(
        (current) => current?.map((notification) => ({ ...notification, status: "READ" })),
        false,
      );

      // Then revalidate to ensure data consistency
      await refreshNotifications();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      await createAnalytic({
        class: "ERROR",
        event: {
          event: "markAllNotificationsAsRead",
          error,
        },
      });
      throw error;
    }
  };

  /**
   * Send direct email to a recipient
   *
   * @param email Recipient email
   * @param subject Email subject
   * @param message Email body
   * @returns Promise with response
   */
  const sendDirectEmail = async (email: string, subject: string, message: string) => {
    return sendEmail(email, subject, message);
  };

  /**
   * Send direct SMS to a recipient
   *
   * @param phoneNumber Recipient phone number
   * @param message SMS message
   * @returns Promise with response
   */
  const sendDirectSMS = async (phoneNumber: string, message: string) => {
    return sendText(phoneNumber, message);
  };

  /**
   * Archive a notification
   *
   * @param notificationId ID of the notification to archive
   * @returns Promise<void>
   */
  const archiveNotification = async (notificationId: number): Promise<void> => {
    try {
      await updateNotification(notificationId, {
        is_read: true,
      });

      await refreshNotifications(
        (current) =>
          current?.filter((notification) => notification.notification_id !== notificationId),
        false,
      );
    } catch (error) {
      console.error("Error archiving notification:", error);
      await createAnalytic({
        class: "ERROR",
        event: {
          event: "archiveNotification",
          error,
          notificationId,
        },
      });
      throw error;
    }
  };

  return {
    // Notification data
    notifications,
    unreadCount,
    isLoading,
    error,

    // CRUD operations
    createNotification: createNewNotification,
    deleteNotification,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    refreshNotifications,

    // Direct communication
    sendEmail: sendDirectEmail,
    sendSMS: sendDirectSMS,

    // Utility
    roleType: effectiveRoleType,
  };
}
